"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const lodash_1 = require("lodash");
const __1 = require("..");
var ElementType;
(function (ElementType) {
    ElementType["AND"] = "AND";
    ElementType["OR"] = "OR";
    ElementType["FILTER"] = "filter";
})(ElementType = exports.ElementType || (exports.ElementType = {}));
class FovConverter {
    constructor() {
        this.comparators = {};
        this.comparators = {
            [__1.ComparisonOperators.MATCH_EQUALS]: (exactlyValue, comparedValue) => {
                return exactlyValue === comparedValue;
            },
            [__1.ComparisonOperators.MATCH_CONTAINS]: (exactlyValue, comparedValue) => {
                return exactlyValue.includes(comparedValue);
            },
            [__1.ComparisonOperators.MATCH_STARTS_WITH]: (exactlyValue, comparedValue) => {
                return exactlyValue.startsWith(comparedValue);
            },
            [__1.ComparisonOperators.MATCH_ENDS_WITH]: (exactlyValue, comparedValue) => {
                return exactlyValue.endsWith(comparedValue);
            },
            [__1.ComparisonOperators.IS_EMPTY]: () => {
                return false;
            },
            [__1.ComparisonOperators.MATCH_REGEX]: (exactlyValue, comparedValue) => {
                return new RegExp(comparedValue.replace(/\\/g, '\\\\')).test(exactlyValue);
            },
        };
    }
    flatten(fov) {
        const finalFov = { nodeType: ElementType.AND, children: [] };
        fov.children.forEach(child => {
            if (!this.isNode(child)) {
                finalFov.children.push(this.wrapLeafInNodeOfType(child, ElementType.OR));
                return;
            }
            if (this.isLastBranch(child)) {
                finalFov.children.push(child);
                return;
            }
            if (this.hasUniqueChild(child)) {
                const [uniqueChild] = child.children;
                switch (uniqueChild.nodeType) {
                    case ElementType.AND:
                        finalFov.children.push(...uniqueChild.children.map(c => this.wrapLeafInNodeOfType(c, ElementType.OR)));
                        return;
                    case ElementType.OR:
                        const newBranch = {
                            nodeType: ElementType.OR,
                            children: [],
                        };
                        newBranch.children.push(...uniqueChild.children);
                        finalFov.children.push(newBranch);
                        return;
                    default:
                        return;
                }
            }
            const children = child.children.map(sChild => {
                if (!this.isNode(sChild)) {
                    return this.wrapLeafInNodeOfType(sChild, ElementType.AND);
                }
                return sChild;
            });
            if (this.hasOrNode(child)) {
                children.forEach(sChild => {
                    switch (sChild.nodeType) {
                        case ElementType.OR:
                            finalFov.children.push({
                                nodeType: ElementType.OR,
                                children: sChild.children.reduce((p, n) => p.concat(n), []),
                            });
                            return;
                        case ElementType.AND:
                            finalFov.children.push(...sChild.children.map(andLeaf => ({
                                nodeType: ElementType.OR,
                                children: [andLeaf],
                            })));
                        default:
                            return;
                    }
                });
                return;
            }
            if (this.allNodesHaveOneChild(child)) {
                finalFov.children.push({
                    nodeType: ElementType.OR,
                    children: children.reduce((p, n) => p.concat(n.children), []),
                });
                return;
            }
            finalFov.children.push(...this.distributeAnd(children));
        });
        return this.optimizeFilters(finalFov);
    }
    distributeAnd([firstNode, ...nodes]) {
        const children = [];
        const nodesToProcess = nodes.map(node => {
            if (this.isNode(node) && this.hasUniqueChild(node)) {
                return {
                    nodeType: node.nodeType,
                    children: [...node.children, ...node.children],
                };
            }
            return node;
        });
        firstNode.children.forEach(child => {
            const leftElements = [];
            const rightElements = [];
            nodesToProcess.forEach(node => {
                leftElements.push(node.children[0]);
                rightElements.push(node.children[1]);
            });
            children.push({
                nodeType: ElementType.OR,
                children: [child, ...leftElements],
            });
            children.push({
                nodeType: ElementType.OR,
                children: [child, ...rightElements],
            });
        });
        return children;
    }
    isLastBranch(branch) {
        return branch.children.every(element => !this.isNode(element));
    }
    isNode(element) {
        return !!element.children;
    }
    hasOrNode(branch) {
        return !!branch.children.find(element => element.nodeType === ElementType.OR);
    }
    allNodesHaveOneChild(branch) {
        let allNodesHaveOneChild = true;
        branch.children.forEach(node => {
            allNodesHaveOneChild = allNodesHaveOneChild && this.hasUniqueChild(node);
        });
        return allNodesHaveOneChild;
    }
    hasUniqueChild(node) {
        return node.children.length === 1;
    }
    wrapLeafInNodeOfType(leaf, nodeType) {
        return {
            nodeType,
            children: [leaf],
        };
    }
    optimizeFilters(node) {
        const childrenToRemove = new Set();
        if (!node.children) {
            return node;
        }
        for (let indexChild = 0; indexChild < node.children.length; indexChild++) {
            const childNode = node.children[indexChild];
            if (childrenToRemove.has(childNode)) {
                continue;
            }
            if (childNode.nodeType !== ElementType.FILTER) {
                this.optimizeFilters(childNode);
                if (childNode.children && childNode.children.length === 0) {
                    childrenToRemove.add(childNode);
                }
                continue;
            }
            for (let indexExactly = 0; indexExactly < node.children.length; indexExactly++) {
                const exactlyNode = node.children[indexExactly];
                if (exactlyNode.nodeType !== ElementType.FILTER ||
                    childrenToRemove.has(exactlyNode) ||
                    !this.isExactlyOperator(exactlyNode.operator) ||
                    indexExactly === indexChild ||
                    exactlyNode.field !== childNode.field) {
                    continue;
                }
                if (this.isUselessNode(exactlyNode, childNode)) {
                    const dropIndex = node.nodeType === ElementType.AND ? indexChild : indexExactly;
                    childrenToRemove.add(node.children[dropIndex]);
                }
                else if (node.nodeType === ElementType.AND) {
                    node.children = [];
                }
            }
        }
        node.children = this.cleanNodes(node.children, childrenToRemove);
        return node;
    }
    isUselessNode(exactlyNode, comparedNode) {
        const matches = comparedNode.operator.match(/^(not_)?(.+)$/);
        if (matches === null) {
            return;
        }
        const not = !!matches[1];
        const operator = matches[2];
        if (!this.comparators[operator]) {
            console.error(`Operator not supported ${operator}`);
        }
        const condition = exactlyNode.operator === __1.ComparisonOperators.IS_EMPTY
            ? operator === __1.ComparisonOperators.IS_EMPTY
            : this.comparators[operator](exactlyNode.value, comparedNode.value);
        return not !== condition;
    }
    cleanNodes(nodes, toRemove) {
        return this.unique(nodes.filter(node => !toRemove.has(node)));
    }
    unique(array) {
        return array.filter((item, index, list) => list.findIndex(a => lodash_1.isEqual(item, a)) === index);
    }
    isExactlyOperator(operator) {
        return operator === __1.ComparisonOperators.IS_EMPTY || operator === __1.ComparisonOperators.MATCH_EQUALS;
    }
    static isEmptyFov(fov) {
        if (typeof fov === 'string') {
            fov = JSON.parse(fov);
        }
        if (fov.nodeType === ElementType.FILTER) {
            return false;
        }
        else {
            return fov.children.length === 0 || !fov.children.some(children => !this.isEmptyFov(children));
        }
    }
}
exports.FovConverter = FovConverter;
//# sourceMappingURL=fov-converter.js.map